import { useFormik } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Progress } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useNotification } from 'hooks/useNotification'
import { refetchFunc } from 'modules/domain/common/hooks'
import { Driver } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { SharedModalInputs } from 'views/components/SharedModal/styled'
import { AnalyticsPlaces } from '../../analyticsPlaces'
import { DriverModalContent } from './DriverModalContent'

interface Props {
  onClose: () => void
  carrierId: string
  onSuccess: refetchFunc
  driver: Driver
}

export const DriverEditModal: FC<Props> = ({ onClose, carrierId, onSuccess, driver }) => {
  const { formProgress, formHandler } = useAFormHandler('editDriver')
  const { t } = useTranslation('logisticsKR')
  const notify = useNotification()

  const handleEditDriver = formHandler(
    async () => {
      await apiClient.put(endpoints.LogisticsKR.drivers(driver?.id), formik.values)
    },
    {
      onSuccess: async () => {
        await onSuccess()
        onClose()
        notify(Progress.SUCCESS, { title: t('carriers.notifications.driver_edit_success') })
      },
      onError: (error) => {
        const { errors } = error
        formik.setErrors(errors)
      },
    },
  )

  const formik = useFormik({
    initialValues: {
      carrier: carrierId,
      name: driver.name,
      phone: driver.phone,
    },
    enableReinitialize: true,
    onSubmit: handleEditDriver,
  })

  return (
    <ASharedModal id={AnalyticsPlaces.CARRIERS.EDIT_DRIVER_MODAL} size="medium" onClose={onClose}>
      <SharedModalTitle>{t('carsDetails.addForm.editTitle')}</SharedModalTitle>
      <SharedModalContent>
        <SharedModalInputs>
          <DriverModalContent formik={formik} isEditModal />
        </SharedModalInputs>
      </SharedModalContent>
      <ADefaultModalFooter
        onClose={onClose}
        progress={formProgress}
        confirmButtonText={t('common:save')}
        onConfirm={() => formik.submitForm()}
      />
    </ASharedModal>
  )
}
